import React from 'react';
import { Page } from '@react-pdf/renderer';
import { getNumber } from 'js/components/number/number';
import { getImpressionThreshold } from 'js/utils';
import { pageSizes } from '../constants';
import Aside from '../components/aside/aside';
import AnnotatedChart from '../components/annotated-chart/annotated-chart';
import Logos from '../components/logos/logos';
import PageContent from '../components/page-content/page-content';
import Groups from '../components/groups/groups';
import NoDataAlert from '../components/no-data-alert/no-data-alert';
import styles from '../report.styles';
import { getDisplayReportRecommendations, getCtr, getViewRate } from '../utils';

export default function DisplayTopicThemesPage({
    orientation,
    logo4D,
    brandLogo,
    charts,
    report,
}) {
    const impressionThreshold = getImpressionThreshold(report.impressions);
    const isPortrait = orientation === 'portrait';
    const pageClasses = [
        styles.page,
        isPortrait ? styles.pagePortrait : styles.pageLandscape,
    ];

    if (logo4D || brandLogo) {
        pageClasses.push(isPortrait ? null : styles.pageLandscapeWithLogo);
    }

    const recommendations = getDisplayReportRecommendations(report).filter(
        (i) => i.title === 'Topic',
    );

    const formatGroups = (groups) =>
        [...groups].map((i) => ({
            icon: i.logobase64 || null,
            label: i.topic_group_name,
            impressions: getNumber({
                value: i.impressions,
                maximumFractionDigits: 0,
            }),
            ctr: getNumber({
                value: getCtr(i),
                isPercentage: true,
                maximumFractionDigits: 2,
            }),
            viewRate:
                getViewRate(report) > 0 &&
                getNumber({
                    value: getViewRate(i),
                    isPercentage: true,
                    maximumFractionDigits: 2,
                }),
        }));

    const topGroupsByCtr = [...report.topic_groups]
        .sort((a, b) => getCtr(b) - getCtr(a))
        .slice(0, 3)
        .filter((item) => item.impressions >= impressionThreshold);

    const topGroupsByViewRate = [...report.topic_groups]
        .sort((a, b) => getViewRate(b) - getViewRate(a))
        .slice(0, 3)
        .filter((item) => item.impressions >= impressionThreshold);

    const showImpChart = report.topic_groups.some(
        (group) => group.impressions >= impressionThreshold,
    );
    const impChart = showImpChart && (
        <AnnotatedChart
            orientation={orientation}
            annotations={[{ title: 'Top Topic Groups by Impressions' }]}
            chart={charts.TOPIC_GROUPS}
        />
    );

    const ctrGroups = topGroupsByCtr.length > 0 && (
        <Groups
            orientation={orientation}
            title="Top Topic Groups by CTR"
            groups={formatGroups(topGroupsByCtr)}
            showGroupTitles
            background="#1CBEC9"
            parameters={['impressions', 'ctr', 'viewRate']}
            mainParameter="ctr"
            hasBorderTop
        />
    );

    const viewRateGroups = getViewRate(report) > 0 &&
        topGroupsByViewRate.length > 0 && (
            <Groups
                orientation={orientation}
                title="Top Topic Groups by View Rate"
                groups={formatGroups(topGroupsByViewRate)}
                showGroupTitles
                background="#4C9AFF"
                parameters={['impressions', 'ctr', 'viewRate']}
                mainParameter="viewRate"
                hasBorderTop
            />
        );

    return (
        <Page
            size={isPortrait ? pageSizes.portrait : pageSizes.landscape}
            orientation={orientation}
            style={pageClasses}
        >
            <Aside
                orientation={orientation}
                title="Topic Themes"
                description="Topics are combinations of keywords that share the same overall theme. Use these insights to understand how contexts overlap with your media, and can improve your targeting & performance."
                hasLogo={Boolean(logo4D || brandLogo)}
                recommendations={recommendations}
            />
            <PageContent orientation={orientation}>
                {impChart || ctrGroups || viewRateGroups ? (
                    <>
                        {impChart}
                        {ctrGroups}
                        {viewRateGroups}
                    </>
                ) : (
                    <NoDataAlert />
                )}
            </PageContent>
            <Logos
                orientation={orientation}
                logo4D={logo4D}
                brandLogo={brandLogo}
            />
        </Page>
    );
}
